// useCustomPage — one shared loader for the storefront's three page-builder
// "chrome" surfaces (CustomHeader / CustomFooter in StorefrontLayout.js and
// CustomHome in StorefrontHome.js), plus a tiny in-memory cache and a
// prefetch entry point for App.js.
//
// WHY THIS EXISTS (flash-of-default-theme bug, 2026-10-11): each of those
// three components used to fetch its own CustomPage in a useEffect AFTER
// mounting, and rendered the theme's built-in STATIC skin (the "default"
// header/hero/footer) for as long as that fetch was in flight — so every
// store using a page-builder theme flashed the default look first and only
// swapped to its real theme when the three requests came back. The same
// flash repeated (a) whenever languageCode changed (first-time visitors
// start with an empty language and get the default one a moment later, which
// re-ran the effect and reset the design to null) and (b) every time the
// shopper navigated back to "/" (CustomHome remounts and refetched from
// scratch). This module fixes all three at the source:
//   1. a module-level cache (+ in-flight request de-duplication) keyed by
//      page id + language, read SYNCHRONOUSLY for the initial render, so a
//      remount or a return to "/" paints the real design on the first frame;
//   2. stale-while-revalidate: a language switch keeps showing the previous
//      design for the same page until the new one arrives, never the static
//      skin;
//   3. prefetchStoreCustomPages(), called by App.js the moment the site
//      context resolves — BEFORE the lazy StorefrontApp chunk has even
//      finished downloading — so the three requests run in parallel with the
//      chunk download instead of after first paint.
// The static fallback is now reserved for a genuine failure (deleted/
// deactivated page, network error, or a request that takes > 8s); while a
// request is merely in flight the callers render a neutral placeholder.
import { useEffect, useState } from "react";
import { getPublicCustomPageById } from "../../services/api";

const STORAGE_KEY = "storefrontLanguage"; // same key StorefrontI18nContext.js writes
const SLOW_MS = 8000;

const cache = new Map(); // `${id}|${lang}` -> { data, promise }

const keyOf = (id, lang) => `${id}|${lang || ""}`;

export function readStoredStorefrontLanguage() {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function loadCustomPage(id, lang) {
  const k = keyOf(id, lang);
  const hit = cache.get(k);
  if (hit) return hit.promise;
  const entry = { data: null, promise: null };
  entry.promise = getPublicCustomPageById(id, lang || undefined)
    .then((page) => {
      entry.data = page;
      return page;
    })
    .catch((err) => {
      cache.delete(k); // never cache a failure — a retry/remount should refetch
      throw err;
    });
  cache.set(k, entry);
  return entry.promise;
}

// peek — synchronous read for the first render. Exact language match wins;
// otherwise any already-loaded copy of the SAME page (another language) is
// returned as stale content rather than nothing.
function peek(id, lang) {
  const exact = cache.get(keyOf(id, lang));
  if (exact && exact.data) return exact.data;
  const prefix = `${id}|`;
  for (const [k, e] of cache) {
    if (k.startsWith(prefix) && e.data) return e.data;
  }
  return null;
}

// prefetchStoreCustomPages — fire-and-forget; safe to call with any store
// shape (including null) and any number of times.
export function prefetchStoreCustomPages(store, lang) {
  if (!store) return;
  const l = lang === undefined ? readStoredStorefrontLanguage() : lang;
  [
    [store.header_custom_page_active, store.header_custom_page_ref],
    [store.home_custom_page_active, store.home_custom_page_ref],
    [store.footer_custom_page_active, store.footer_custom_page_ref],
  ].forEach(([active, ref]) => {
    const id = Number(ref || 0);
    if (active && id > 0) loadCustomPage(id, l).catch(() => {});
  });
}

// returns { design, failed }:
//   design — the CustomPage (possibly a stale-language copy) or null
//   failed — true only once the request errored or is taking > SLOW_MS AND
//            there is no design to show; callers render their static
//            fallback only then, and a neutral placeholder while
//            design === null && !failed.
export default function useCustomPage(pageId, languageCode) {
  const [state, setState] = useState(() => ({ pageId, data: peek(pageId, languageCode) }));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    const cached = peek(pageId, languageCode);
    setState((prev) => {
      if (cached) return { pageId, data: cached };
      return prev.pageId === pageId ? prev : { pageId, data: null };
    });
    const timer = setTimeout(() => {
      if (!cancelled) setFailed(true);
    }, SLOW_MS);
    loadCustomPage(pageId, languageCode)
      .then((page) => {
        if (cancelled) return;
        setState({ pageId, data: page });
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pageId, languageCode]);

  const design = state.pageId === pageId ? state.data : peek(pageId, languageCode);
  return { design, failed: failed && !design };
}
